import React from 'react';
import { SubManager } from '../../classes/SubManager';
import { AsperaError } from '../../classes/AsperaError';
import { language } from '../../services/LanguageService';
import { modal } from '../../services/ModalService';
import { AsperaForm } from '../AsperaForm';
import { AsperaWrapper } from '../AsperaWrapper';
import { ReplaySubject, Subject } from 'rxjs';
import { Button, FileUploaderItem } from '../Carbon';
import { ModalFormRender } from '../ModalFormRender';
/**
 * React Components
 *
 * The FaspioBridge view component renders the bridge list
 *
 * @example
 * ```javascript
 * <FaspioBridges serverUrl="" />
 * ```
 */
export class FaspioBridgeForm extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            internalLoading: false,
        };
        this.subManager = new SubManager();
        this.disableOverride = new ReplaySubject(1);
        this.initiationSub = new Subject();
        this.saveForm = () => {
            const { bridge, onAfterSave } = this.props;
            this.setState({ internalLoading: true });
            this.subManager.setSubscription(bridge.save().subscribe({
                next: newBridge => {
                    if (typeof onAfterSave === 'function') {
                        onAfterSave(newBridge);
                    }
                    this.setState({ internalLoading: false });
                },
                error: error => {
                    new AsperaError('ApiClientDetails: Unable to get API client', error);
                    this.setState({ internalLoading: false });
                },
            }));
        };
        this.validHost = (host) => {
            return !!(typeof host === 'string' && host && !host.includes(':'));
        };
        this.validateForm = () => {
            const { bridge } = this.props;
            this.disableOverride.next(!bridge.isValid);
        };
        this.addPort = () => {
            const { bridge } = this.props;
            const modalInfo = modal.prepare();
            const disableSubject = new Subject();
            const modalProps = {
                modalHeading: language.get('faspio.addPortForwarding'),
                primaryButtonText: language.get('faspio.add'),
            };
            const portModel = {
                local: '80',
                forward: '80',
            };
            const modalBodyProps = {
                modalClose: modalInfo.callback,
                disableSubject: disableSubject,
                formData: {
                    id: 'faspio-form-add-port',
                    model: portModel,
                    items: [
                        {
                            type: 'number',
                            pointer: 'local',
                            required: true,
                            label: language.get('faspio.localPort'),
                            numberProps: { min: 0 },
                        },
                        {
                            type: 'number',
                            pointer: 'forward',
                            required: true,
                            label: language.get('faspio.forwardPort'),
                            numberProps: { min: 0 },
                        },
                    ],
                },
            };
            this.subManager.setSubscription(modal.create(modalInfo.id, modalProps, React.createElement(ModalFormRender, Object.assign({}, modalBodyProps)), disableSubject.asObservable()).subscribe({
                next: () => {
                    bridge.addPort(Number(portModel.local || 0), Number(portModel.forward || 0));
                    this.setState({});
                    this.validateForm();
                },
                error: () => { },
            }));
        };
    }
    get loading() {
        const { loading } = this.props;
        const { internalLoading } = this.state;
        return loading || internalLoading;
    }
    get portList() {
        const { bridge } = this.props;
        if (bridge.portForwardList.length) {
            return bridge.portForwardList.map((item, index) => {
                return (React.createElement(FileUploaderItem, { className: "cds--file__selected-file", status: "edit", key: index, "data-auto": `faspio-added-port-${index}`, name: language.get('faspio.portForwardItem', [item.local, item.forward]), onDelete: () => {
                        bridge.removePort(index);
                        this.setState({});
                        this.validateForm();
                    } }));
            });
        }
        else {
            return null;
        }
    }
    get formData() {
        const { bridge } = this.props;
        return {
            id: 'faspio-bridge-edit-form',
            model: bridge,
            submitText: language.get(`faspio.${bridge.isCreated() ? 'save' : 'create'}`),
            onSubmit: this.saveForm,
            items: [
                {
                    type: 'text',
                    pointer: 'name',
                    onChange: this.validateForm,
                    required: true,
                    label: language.get('faspio.name'),
                },
                {
                    type: 'header',
                    label: language.get('faspio.local'),
                },
                {
                    type: 'text',
                    pointer: 'localHost',
                    required: true,
                    errorText: language.get('faspio.hostError'),
                    onChange: this.validateForm,
                    validationCheck: host => this.validHost(host),
                    label: language.get('faspio.host'),
                    helperText: language.get('faspio.hostHelper'),
                },
                {
                    type: 'select',
                    pointer: 'local.protocol',
                    required: true,
                    label: language.get('faspio.protocol'),
                    onChange: this.validateForm,
                    errorText: language.get('faspio.protocolError'),
                    validationCheck: value => {
                        return !((value === 'tcp' && bridge.forward.protocol === 'udp') ||
                            (value === 'udp' && bridge.forward.protocol === 'tcp'));
                    },
                    items: [
                        { text: language.get('faspio.fasp'), value: 'fasp' },
                        { text: language.get('faspio.tcp'), value: 'tcp' },
                        { text: language.get('faspio.udp'), value: 'udp' },
                    ],
                },
                {
                    type: 'toggle',
                    pointer: 'local.tls_enabled',
                    onChange: this.validateForm,
                    label: language.get('faspio.tlsEnabled'),
                },
                {
                    type: 'header',
                    label: language.get('faspio.forward'),
                },
                {
                    type: 'autocomplete',
                    hideOptional: true,
                    label: language.get('faspio.host'),
                    autoCompleteProps: {
                        listView: true,
                        helperText: language.get('faspio.hostHelper'),
                        initialItems: bridge.forwardHosts,
                        id: 'faspio-bridge-forward-hosts-autocomplete',
                        resultListSub: this.initiationSub.asObservable(),
                        onChange: items => {
                            bridge.forwardHosts = items;
                            this.validateForm();
                        },
                        onSearchChange: (query) => {
                            this.initiationSub.next([{
                                    results: this.validHost(query) ? [{ primaryId: query, displayName: query }] : [],
                                }]);
                        },
                    },
                },
                {
                    type: 'select',
                    pointer: 'forward.protocol',
                    required: true,
                    label: language.get('faspio.protocol'),
                    errorText: language.get('faspio.protocolError'),
                    onChange: this.validateForm,
                    validationCheck: value => {
                        return !((value === 'tcp' && bridge.local.protocol === 'udp') ||
                            (value === 'udp' && bridge.local.protocol === 'tcp'));
                    },
                    items: [
                        { text: language.get('faspio.fasp'), value: 'fasp' },
                        { text: language.get('faspio.tcp'), value: 'tcp' },
                        { text: language.get('faspio.udp'), value: 'udp' },
                    ],
                },
                {
                    type: 'toggle',
                    onChange: this.validateForm,
                    pointer: 'forward.tls_enabled',
                    label: language.get('faspio.tlsEnabled'),
                },
                {
                    type: 'header',
                    label: language.get('faspio.portForwarding'),
                },
                {
                    type: 'custom',
                    customView: React.createElement(Button, { kind: "tertiary", onClick: this.addPort }, language.get('faspio.addPortForwarding')),
                },
                {
                    type: 'custom',
                    customView: this.portList,
                },
            ],
        };
    }
    componentDidUpdate(prevProps) {
        const { bridge, serverUrl, id } = this.props;
        if (bridge.id !== prevProps.bridge.id ||
            serverUrl !== prevProps.serverUrl ||
            id !== prevProps.id) {
            this.validateForm();
        }
    }
    componentDidMount() {
        this.validateForm();
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    render() {
        const { className, pageHeaderProps, error, bridge, id } = this.props;
        return (React.createElement(AsperaWrapper, { error: error, loading: this.loading, className: `faspio-bridge-edit ${className ? className : ''}`, pageHeaderProps: pageHeaderProps, "data-auto": "faspio-bridge-edit", id: id, fullScreenMode: (!this.loading && !bridge.isCreated()) },
            React.createElement(AsperaForm, { formDetails: this.formData, fullPageWrapperId: id, disableOverride: this.disableOverride.asObservable() })));
    }
}
